import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';

import axiosClient from '../../api/axios-client';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import Screen from '../components/Screen';
import Text from '../components/Text';
import { chargerLangue, langueMobile, LANGUES_MOBILES, NOMS_LANGUES, t, type LangueMobile } from '../i18n/langue';

/**
 * Le choix de la langue de l'app, hors registre comme `/notifications` : une
 * sous-vue du compte, pas un écran de travail.
 *
 * Chaque langue est écrite dans sa propre langue (`NOMS_LANGUES`) : une femme de
 * chambre qui ne lit pas le français reconnaît « ქართული ». L'enregistrement
 * précède la bascule : la langue affichée est toujours celle que le serveur
 * utilisera pour les pushs.
 */
export default function Langue() {
    const navigate = useNavigate();
    const courante = langueMobile();
    const [enCours, setEnCours] = useState<LangueMobile | null>(null);
    const [echec, setEchec] = useState(false);

    const handleChoisir = async (langue: LangueMobile) => {
        if (langue === courante || enCours) return;
        setEnCours(langue);
        setEchec(false);
        try {
            await axiosClient.patch('/user/app-locale', { app_locale: langue });
            await chargerLangue(langue);
        } catch {
            setEchec(true);
        } finally {
            setEnCours(null);
        }
    };

    return (
        <Screen>
            <header className="flex shrink-0 items-center gap-2 border-b border-border-color-light bg-white px-3 py-2">
                <IconButton libelle={t('commun.retour')} onPress={() => navigate(-1)}>
                    <ArrowLeftIcon className="h-6 w-6 text-text-color" />
                </IconButton>
                <Text variante="sousTitre" as="h1">
                    {t('langue.titre')}
                </Text>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8 pt-4">
                <Text variante="petit" ton="attenue">
                    {t('langue.explication')}
                </Text>
                {echec && (
                    <Text variante="petit" ton="danger" className="mt-2">
                        {t('langue.echec')}
                    </Text>
                )}
                <ul className="mt-4 overflow-hidden rounded-2xl bg-white">
                    {LANGUES_MOBILES.map((langue) => (
                        <li key={langue} lang={langue} className="border-b border-border-color-light last:border-b-0">
                            <Button
                                libelle={langue === courante ? t('langue.choisie', { langue: NOMS_LANGUES[langue] }) : NOMS_LANGUES[langue]}
                                icone={langue === courante ? CheckIcon : undefined}
                                variante="discret"
                                alignement="debut"
                                pleineLargeur
                                disabled={enCours !== null}
                                onPress={() => void handleChoisir(langue)}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </Screen>
    );
}
